import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Shared list/pagination logic for the portal Orders / POs / Inquiries pages.
 *
 * @param {(params:object)=>Promise<{success:boolean,data?:object,error?:string}>} fetcher
 * @param {object} filters - { status, from_date, to_date, search } (search filtered client-side)
 * @param {number} page - 1-indexed
 * @param {number} pageSize
 */
export default function usePortalList(fetcher, filters, page, pageSize) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const reqIdRef = useRef(0);

  const { status, from_date, to_date } = filters || {};

  const load = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    const params = { skip: (page - 1) * pageSize, limit: pageSize };
    if (status) params.status = status;
    if (from_date) params.from_date = from_date;
    if (to_date) params.to_date = to_date;

    const res = await fetcher(params);
    if (reqId !== reqIdRef.current) return; // a newer request superseded this one
    if (res.success) {
      const data = res.data || {};
      setItems(Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []));
      setTotal(typeof data.total === 'number' ? data.total : (Array.isArray(data.items) ? data.items.length : 0));
    } else {
      setError(res.error || 'Failed to load.');
      setItems([]);
      setTotal(0);
    }
    setLoading(false);
  }, [fetcher, status, from_date, to_date, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return { items, total, totalPages, loading, error, reload: load };
}
